// MaturaRecursiveSequencePFClosedTask.js
// Zadanie maturalne zamkniete P/F: arytmetycznosc i geometrycznosc ciagu z rekurencji.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TruthInput from "../../../../components/inputs/TruthInput";

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function signedLatex(n) {
  if (n === 0) return "";
  return n > 0 ? `+${n}` : `-${Math.abs(n)}`;
}

function termLatex(k) {
  if (k === 1) return "a_n";
  if (k === -1) return "-a_n";
  return `${k}a_n`;
}

function factorLatex(n) {
  return n < 0 ? `(${n})` : String(n);
}

function greatestCommonDivisor(a, b) {
  let left = Math.abs(a);
  let right = Math.abs(b);
  while (right) {
    [left, right] = [right, left % right];
  }
  return left || 1;
}

function ratioLatex(numerator, denominator) {
  const divisor = greatestCommonDivisor(numerator, denominator);
  let reducedNumerator = numerator / divisor;
  let reducedDenominator = denominator / divisor;
  if (reducedDenominator < 0) {
    reducedNumerator *= -1;
    reducedDenominator *= -1;
  }
  if (reducedDenominator === 1) return String(reducedNumerator);
  return `\\frac{${reducedNumerator}}{${reducedDenominator}}`;
}

function buildValues(a1, k, b, count = 4) {
  const values = [a1];
  for (let i = 2; i <= count; i += 1) {
    values.push(k * values[i - 2] + b);
  }
  return values;
}

function buildVariant() {
  const variant = pick(["arithmetic", "geometric", "neither"]);

  if (variant === "arithmetic") {
    const a1 = randInt(-5, 6);
    const b = pick([-5, -4, -3, -2, -1, 1, 2, 3, 4, 5]);
    return {
      variant,
      a1,
      k: 1,
      b,
      isArithmetic: true,
      isGeometric: false,
    };
  }

  if (variant === "geometric") {
    return {
      variant,
      a1: pick([-5, -4, -3, -2, -1, 1, 2, 3, 4, 5]),
      k: pick([-3, -2, 2, 3, 4]),
      b: 0,
      isArithmetic: false,
      isGeometric: true,
    };
  }

  for (let attempt = 0; attempt < 100; attempt += 1) {
    const a1 = randInt(-5, 6);
    const k = pick([-3, -2, 2, 3, 4]);
    const b = pick([-5, -4, -3, -2, -1, 1, 2, 3, 4, 5]);
    const a2 = k * a1 + b;

    // Avoid a constant sequence, because it would be both arithmetic and geometric.
    if (a2 !== a1 && Math.max(...buildValues(a1, k, b).map(Math.abs)) <= 120) {
      return {
        variant,
        a1,
        k,
        b,
        isArithmetic: false,
        isGeometric: false,
      };
    }
  }

  return {
    variant: "neither",
    a1: 2,
    k: 2,
    b: 1,
    isArithmetic: false,
    isGeometric: false,
  };
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaRecursiveSequencePFClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: "14.2",
    },
    data: {
      variant: "neither",
      a1: 2,
      k: 2,
      b: 1,
      isArithmetic: false,
      isGeometric: false,
      values: [2, 5, 11, 23],
      recurrenceLatex: "a_{n+1}=2a_n+1",
      maxPoints: 1,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_MAJ") {
    return generateMay2025OriginalTask();
  }

  const desc = buildVariant();
  const values = buildValues(desc.a1, desc.k, desc.b);

  return {
    type: "MaturaRecursiveSequencePFClosedTask",
    data: {
      ...desc,
      values,
      recurrenceLatex: `a_{n+1}=${termLatex(desc.k)}${signedLatex(desc.b)}`,
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  const { a1, recurrenceLatex } = data;

  return (
    <div style={{ textAlign: "left" }} className="space-y-2">
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        Ciąg <InlineMath math={"(a_n)"} /> jest określony następująco:
      </p>

      <BlockMath
        math={`\\begin{cases}a_1=${a1}\\\\${recurrenceLatex}\\end{cases}`}
      />
      <p>Wzór rekurencyjny obowiązuje dla każdej liczby naturalnej <InlineMath math="n\geq1" />.</p>

      <p>
        Oceń prawdziwość poniższych stwierdzeń. Wybierz{" "}
        <InlineMath math={"P"} />, jeśli stwierdzenie jest prawdziwe, albo{" "}
        <InlineMath math={"F"} />, jeśli jest fałszywe.
      </p>

      <p>
        <strong>1.</strong> Ciąg <InlineMath math={"(a_n)"} /> jest arytmetyczny.
      </p>
      <p>
        <strong>2.</strong> Ciąg <InlineMath math={"(a_n)"} /> jest geometryczny.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};

  const setValue = (key, v) => {
    onChange?.({
      ...value,
      [key]: v,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>1.</span>
        <TruthInput
          value={value.s1 || null}
          disabled={disabled}
          onChange={(v) => setValue("s1", v)}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>2.</span>
        <TruthInput
          value={value.s2 || null}
          disabled={disabled}
          onChange={(v) => setValue("s2", v)}
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  const correctS1 = data.isArithmetic ? "P" : "F";
  const correctS2 = data.isGeometric ? "P" : "F";
  const { s1, s2 } = userAnswer;

  if (s1 !== "P" && s1 !== "F") return false;
  if (s2 !== "P" && s2 !== "F") return false;

  return s1 === correctS1 && s2 === correctS2;
}

export function renderAnswer({ data }) {
  const s1 = data.isArithmetic ? "P" : "F";
  const s2 = data.isGeometric ? "P" : "F";

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>Poprawna odpowiedź:</p>
      <p>
        <strong>1.</strong> <InlineMath math={`\\text{${s1}}`} />
      </p>
      <p>
        <strong>2.</strong> <InlineMath math={`\\text{${s2}}`} />
      </p>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) {
    return <i>(brak odpowiedzi)</i>;
  }

  const s1 = userAnswer.s1 || "-";
  const s2 = userAnswer.s2 || "-";

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>Twoja odpowiedź:</p>
      <p>
        1. <InlineMath math={s1 === "-" ? "-" : `\\text{${s1}}`} />
      </p>
      <p>
        2. <InlineMath math={s2 === "-" ? "-" : `\\text{${s2}}`} />
      </p>
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a1, k, b, values, recurrenceLatex, isArithmetic, isGeometric } = data;
  const [v1, v2, v3, v4] = values;
  const d1 = v2 - v1;
  const d2 = v3 - v2;
  const ratioCalculations = values
    .slice(0, -1)
    .map((denominator, index) => ({
      denominator,
      numerator: values[index + 1],
      currentIndex: index + 1,
      nextIndex: index + 2,
    }))
    .filter(({ denominator }) => denominator !== 0)
    .slice(0, 2);

  const correctS1 = isArithmetic ? "P" : "F";
  const correctS2 = isGeometric ? "P" : "F";

  return (
    <div style={{ textAlign: "left" }} className="space-y-3">
      <p>
        Zaczynamy od kilku kolejnych wyrazów ciągu:
      </p>
      <BlockMath math={`a_1=${a1},\\quad ${recurrenceLatex}`} />
      <BlockMath
        math={`a_2=${k}\\cdot${factorLatex(v1)}${signedLatex(b)}=${v2}`}
      />
      <BlockMath
        math={`a_3=${k}\\cdot${factorLatex(v2)}${signedLatex(b)}=${v3}`}
      />
      <BlockMath
        math={`a_4=${k}\\cdot${factorLatex(v3)}${signedLatex(b)}=${v4}`}
      />

      <p>
        <strong>Stwierdzenie 1.</strong> Ciąg jest arytmetyczny, gdy kolejne
        różnice są stałe.
      </p>
      <BlockMath math={`a_2-a_1=${v2}-${factorLatex(v1)}=${d1}`} />
      <BlockMath math={`a_3-a_2=${v3}-${factorLatex(v2)}=${d2}`} />
      {isArithmetic ? (
        <p>
          Tu wzór ma postać <InlineMath math={`a_{n+1}=a_n${signedLatex(b)}`} />,
          więc za każdym razem dodajemy tę samą liczbę. Stwierdzenie jest
          prawdziwe.
        </p>
      ) : (
        <p>
          Różnice nie są stałe, więc ciąg nie jest arytmetyczny. Stwierdzenie
          jest fałszywe.
        </p>
      )}

      <p>
        <strong>Stwierdzenie 2.</strong> Ciąg jest geometryczny, gdy każdy
        kolejny wyraz powstaje przez pomnożenie poprzedniego przez tę samą
        liczbę.
      </p>
      {ratioCalculations.map((calculation) => (
        <BlockMath
          key={calculation.currentIndex}
          math={`\\frac{a_${calculation.nextIndex}}{a_${calculation.currentIndex}}=\\frac{${calculation.numerator}}{${calculation.denominator}}=${ratioLatex(calculation.numerator, calculation.denominator)}`}
        />
      ))}
      {isGeometric ? (
        <p>
          Oba ilorazy są równe <InlineMath math={String(k)} />. Oznacza to, że
          każdy kolejny wyraz otrzymujemy przez pomnożenie poprzedniego przez{" "}
          <InlineMath math={String(k)} />. Ciąg jest geometryczny, więc
          stwierdzenie jest prawdziwe.
        </p>
      ) : (
        <p>
          Ilorazy nie są jednakowe, czyli nie ma jednej stałej liczby, przez
          którą za każdym razem mnożymy poprzedni wyraz. Ciąg nie jest
          geometryczny, więc stwierdzenie jest fałszywe.
        </p>
      )}

      <p>
        Poprawne odpowiedzi:{" "}
        <InlineMath math={`1.\\ \\text{${correctS1}}`} /> oraz{" "}
        <InlineMath math={`2.\\ \\text{${correctS2}}`} />.
      </p>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  renderUserAnswer,
  checkAnswer,
  renderAnswer,
  renderExplanation,
};
